import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Input from '../../components/input/Input';
import DatePicker from '../../components/datepicker/DatePicker';
import DropDown from '../../components/dropdown/DropDown';
import { MonetizationFormData, FormValues } from '../../pages/MonetizationNew';
import { calculateTermInDays } from '../../utils/dateCalculations';
import { useEffect } from 'react';

interface LoanSectionProps {
  formData: MonetizationFormData;
  formValues: FormValues;
  updateFormValues: (updates: Partial<FormValues>) => void;
  operationType?: string;
}

const LoanSection = ({ formData, formValues, updateFormValues, operationType }: LoanSectionProps) => {
  // Update term_in_days when dates change
  useEffect(() => {
    if (formValues.issue_date && formValues.due_date) {
      const termInDays = calculateTermInDays(formValues.issue_date, formValues.due_date);
      updateFormValues({ term_in_days: termInDays });
    }
  }, [formValues.issue_date, formValues.due_date, updateFormValues]);

  const creditorItems = formData.creditors?.map(creditor => ({
    label: creditor.name,
    disabled: false
  })) || [];

  const amortizationItems = formData.amortization_types?.map(type => ({
    label: type.name,
    disabled: false
  })) || [];

  const paymentFrequencyItems = formData.payment_frequency_types?.map(type => ({
    label: type.name,
    disabled: false
  })) || [];

  const rateTypeItems = formData.rate_types?.map(type => ({
    label: type.name,
    disabled: false
  })) || [];

  const purposeTypeItems = formData.purpose_types?.map(type => ({
    label: type.name,
    disabled: false
  })) || [];

  const handleCreditorChange = (selectedLabel: string) => {
    const creditor = formData.creditors?.find(c => c.name === selectedLabel);
    if (creditor) {
      updateFormValues({ creditor_id: creditor.id });
    }
  };

  const handleAmortizationChange = (selectedLabel: string) => {
    const amortization = formData.amortization_types?.find(a => a.name === selectedLabel);
    if (amortization) {
      updateFormValues({ amortization_type_id: amortization.id });
    }
  };

  const handlePaymentFrequencyChange = (selectedLabel: string) => {
    const paymentFreq = formData.payment_frequency_types?.find(p => p.name === selectedLabel);
    if (paymentFreq) {
      updateFormValues({ payment_frequency_type_id: paymentFreq.id });
    }
  };

  const handleRateTypeChange = (selectedLabel: string) => {
    const rateType = formData.rate_types?.find(r => r.name === selectedLabel);
    if (rateType) {
      updateFormValues({ rate_type_id: rateType.id });
    }
  };

  const handlePurposeTypeChange = (selectedLabel: string) => {
    const purposeType = formData.purpose_types?.find(p => p.name === selectedLabel);
    if (purposeType) {
      updateFormValues({ purpose_type_id: purposeType.id });
    }
  };

  // Helper functions to get selected values for dropdowns
  const getSelectedCreditor = () => {
    const creditor = formData.creditors?.find(c => c.id === formValues.creditor_id);
    return creditor?.name || 'Seleccionar';
  };

  const getSelectedAmortization = () => {
    const amortization = formData.amortization_types?.find(a => a.id === formValues.amortization_type_id);
    return amortization?.name || 'Seleccionar';
  };

  const getSelectedPaymentFrequency = () => {
    const paymentFreq = formData.payment_frequency_types?.find(p => p.id === formValues.payment_frequency_type_id);
    return paymentFreq?.name || 'Seleccionar';
  };

  const getSelectedRateType = () => {
    const rateType = formData.rate_types?.find(r => r.id === formValues.rate_type_id);
    return rateType?.name || 'Seleccionar';
  };

  const getSelectedPurposeType = () => {
    const purposeType = formData.purpose_types?.find(p => p.id === formValues.purpose_type_id);
    return purposeType?.name || 'Seleccionar';
  };

  return (
    <>
      {/* Datos del crédito */}
      <Card margin={{ bottom: '4' }}>
        <Card margin={{ bottom: '3' }}>
          <H variant="secondary-2" type='4' weight='bold' modifier='strong'>
            Datos del crédito
          </H>
        </Card>
        
        {/* Fila 1 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px', alignItems: 'end' }}>
          <Input
            label="Monto bruto *"
            type="number"
            value={formValues.monetization_amount?.toString() || ''}
            onChange={(e) => updateFormValues({ monetization_amount: parseFloat(e.target.value) || null })}
            size="md"
            inputStatus="normal"
          />
          <Input
            label="Divisa"
            value={formData.currency}
            readOnly
            size="md"
            modifier="weak"
          />
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Fecha de emisión *
            </P>
            <Card margin={{ top: '2' }}>
              <DatePicker
                defaultValue={formValues.issue_date}
                onChange={(date: string) => updateFormValues({ issue_date: date })}
                disabled={false}
                displayFormat="DD/MM/AA"
              />
            </Card>
          </Card>
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Fecha de vencimiento *
            </P>
            <Card margin={{ top: '2' }}>
              <DatePicker
                defaultValue={formValues.due_date}
                onChange={(date: string) => updateFormValues({ due_date: date })}
                disabled={false}
                displayFormat="DD/MM/AA"
              />
            </Card>
          </Card>
        </div>

        {/* Fila 2 */}
        <Card margin={{ top: '3' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', alignItems: 'end' }}>
            <DropDown
              id="creditor"
              label="Banco acreedor *"
              title={getSelectedCreditor()}
              items={creditorItems}
              onChange={handleCreditorChange}
              size="md"
              marginTop="4px"
            />
            <Input
              label="Nro de operación banco"
              value={formValues.bank_operation_number || ''}
              onChange={(e) => updateFormValues({ bank_operation_number: e.target.value })}
              size="md"
              maxLength={250}
              inputStatus="normal"
              readOnly={operationType === 'Operaciones Públicas'}
              modifier={operationType === 'Operaciones Públicas' ? 'weak' : undefined}
            />
            <DropDown
              id="amortization"
              label="Sistema de amortización *"
              title={getSelectedAmortization()}
              items={amortizationItems}
              onChange={handleAmortizationChange}
              size="md"
              marginTop="4px"
            />
            <DropDown
              id="payment_frequency"
              label="Periodicidad de pagos *"
              title={getSelectedPaymentFrequency()}
              items={paymentFrequencyItems}
              onChange={handlePaymentFrequencyChange}
              size="md"
              marginTop="4px"
            />
          </div>
        </Card>

        {/* Fila 3 */}
        <Card margin={{ top: '3' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', alignItems: 'end' }}>
            <Input
              label="Plazo en días *"
              value={calculateTermInDays(formValues.issue_date || '', formValues.due_date || '').toString()}
              readOnly
              size="md"
              modifier="weak"
            />
            <DropDown
              id="purpose_type"
              label="Destino *"
              title={getSelectedPurposeType()}
              items={purposeTypeItems}
              onChange={handlePurposeTypeChange}
              size="md"
              marginTop="4px"
            />
          </div>
        </Card>
      </Card>

      {/* Tasas y gracia */}
      <Card margin={{ bottom: '4' }}>
        <Card margin={{ bottom: '3' }}>
          <H variant="secondary-2" type='4' weight='bold' modifier='strong'>
            Tasas y gracia
          </H>
        </Card>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', alignItems: 'end' }}>
          <DropDown
            id="rate_type"
            label="Tasa *"
            title={getSelectedRateType()}
            items={rateTypeItems}
            onChange={handleRateTypeChange}
            size="md"
            marginTop="4px"
          />
          <Input
            label="Puntos porcentuales *"
            type="number"
            value={formValues.percentage_point?.toString() || ''}
            onChange={(e) => updateFormValues({ percentage_point: parseFloat(e.target.value) || null })}
            size="md"
            inputStatus="normal"
          />
          <Input
            label="Periodo de gracia *"
            type="number"
            value={formValues.grace_period?.toString() || ''}
            onChange={(e) => updateFormValues({ grace_period: parseInt(e.target.value) || null })}
            size="md"
            onlyNumbers
            inputStatus="normal"
          />
        </div>
      </Card>
    </>
  );
};

export default LoanSection;